import { useCallback, useEffect, useMemo, useState } from "react";
import { showWarning } from "@infrastructure/api/utils/toastNotifications";
import { useFinanceCategoryOptions } from "@features/admin/finance/category/hooks/useFinanceCategoryOptions";
import { useDeductibleCatalog } from "@features/admin/finance/hooks/useDeductibleCatalog";
import {
    buildDeductiblePayload,
    buildEditorRows,
    editorValueFromConfigs,
    mapDeductibleErrors,
} from "@features/admin/finance/deductibleConfigUtils";
import {
    DeductibleConfig,
    DeductibleConfigInput,
    DeductibleEditorValue,
    DeductibleRowErrors,
    FinanceConfigSaveResponse,
} from "@features/admin/finance/types/deductibleConfig";

/**
 * Deductible editor state for the category and allocation rule modals: catalog, calc types,
 * editor value and server error mapping.
 */
export function useDeductibleConfigForm(show: boolean, configs?: DeductibleConfig[] | null) {
    const { catalog, loading: catalogLoading, error: catalogError } = useDeductibleCatalog(show);
    const { calcTypes, calcTypesLoading, calcTypesError, fetchCalcTypes } = useFinanceCategoryOptions();

    const [value, setValue] = useState<DeductibleEditorValue>({});
    const [rowErrors, setRowErrors] = useState<DeductibleRowErrors>({});
    const [generalErrors, setGeneralErrors] = useState<string[]>([]);

    useEffect(() => { if (show) fetchCalcTypes(); }, [show, fetchCalcTypes]);

    useEffect(() => {
        if (!show) return;
        setValue(editorValueFromConfigs(configs ?? []));
        setRowErrors({});
        setGeneralErrors([]);
    }, [show, configs]);

    const rows = useMemo(() => buildEditorRows(catalog, configs ?? []), [catalog, configs]);

    const handleChange = useCallback((next: DeductibleEditorValue) => {
        setValue(next);
        setRowErrors({});
        setGeneralErrors([]);
    }, []);

    const buildPayload = useCallback((): DeductibleConfigInput[] => buildDeductiblePayload(rows, value), [rows, value]);

    /** Shows server errors on their rows; returns every message for the validation modal. */
    const applyServerErrors = useCallback((errors: Record<string, unknown> | null | undefined, payload: DeductibleConfigInput[]): string[] => {
        const mapped = mapDeductibleErrors(errors, payload);
        setRowErrors(mapped.rowErrors);
        setGeneralErrors(mapped.general);
        const rowMessages = Object.values(mapped.rowErrors).flatMap((e) => Object.values(e).filter(Boolean) as string[]);
        return [...mapped.general, ...rowMessages];
    }, []);

    const reset = useCallback(() => {
        setValue(editorValueFromConfigs(configs ?? []));
        setRowErrors({});
        setGeneralErrors([]);
    }, [configs]);

    const editorProps = {
        rows,
        value,
        onChange: handleChange,
        calcTypes,
        errors: rowErrors,
        generalErrors,
        loading: catalogLoading || calcTypesLoading,
        loadError: catalogError ?? (calcTypesError ? "Failed to load calculation types." : null),
    };

    return { editorProps, buildPayload, applyServerErrors, reset };
}

/** Shows each save warning (for example a band where nothing is left for the church) as a toast. */
export function showSaveWarnings(data: FinanceConfigSaveResponse | null | undefined): void {
    for (const warning of data?.warnings ?? []) {
        if (warning) showWarning(warning);
    }
}
